import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  addModuleSystem,
  removeModuleSystem,
  installModuleSoftware,
  uninstallModuleSoftware,
  startModuleSoftware,
  stopModuleSoftware,
  setExpertSkill,
  setTranslatorLanguage,
  setComputerOptions,
  setDatabaseTopic,
  setVirtualCrewRoles,
  setVirtualGunnerRoles,
} from "../../../store/features/moduleSlice";
import { COMPUTERS, COMPUTER_OPTIONS } from "../../../constants/computer";
import { SOFTWARE_PACKAGES } from "../../../constants/software";


/**
 * Module Computer Step Component
 * Handles computer system selection and software configuration for modules
 */
export const ModuleComputerStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.computer || []);
  const softwareState = useAppSelector((state) => state.module.moduleConfig?.systems.software);

  // State for computer selection
  const [primaryComputer, setPrimaryComputer] = useState<string>("");
  const [backupComputer, setBackupComputer] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"computer" | "software">("computer");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  // Get current computers
  const currentPrimary = installedSystems.find((comp: any) => comp.role === "primary");
  const currentBackup = installedSystems.find((comp: any) => comp.role === "backup");

  const handleAddPrimaryComputer = (computerKey: string) => {
    if (!computerKey) return;
    
    const computer = COMPUTERS[computerKey as keyof typeof COMPUTERS];
    if (!computer) return;

    // Remove existing primary if any
    if (currentPrimary) {
      dispatch(removeModuleSystem({
        systemType: "computer",
        systemId: currentPrimary.id,
      }));
    }

    dispatch(addModuleSystem({
      systemType: "computer",
      system: {
        ...computer,
        id: `${computer.model}_primary_${Date.now()}`,
        role: "primary",
      },
    }));

    setPrimaryComputer("");
  };

  const handleAddBackupComputer = (computerKey: string) => {
    if (!computerKey) return;
    
    const computer = COMPUTERS[computerKey as keyof typeof COMPUTERS];
    if (!computer) return;

    // Check if backup has lower processing than primary
    if (currentPrimary && computer.processing >= currentPrimary.processing) {
      alert("Backup computer must have lower Processing score than primary computer");
      return;
    }

    // Remove existing backup if any
    if (currentBackup) {
      dispatch(removeModuleSystem({
        systemType: "computer",
        systemId: currentBackup.id,
      }));
    }

    dispatch(addModuleSystem({
      systemType: "computer",
      system: {
        ...computer,
        id: `${computer.model}_backup_${Date.now()}`,
        role: "backup",
      },
    }));

    setBackupComputer("");
  };

  const handleRemoveComputer = (computerId: string) => {
    dispatch(removeModuleSystem({
      systemType: "computer",
      systemId: computerId,
    }));
  };

  const handleInstallSoftware = (packageId: string, level: number) => {
    dispatch(installModuleSoftware({ packageId, level }));
  };

  const handleUninstallSoftware = (packageId: string, level: number) => {
    dispatch(uninstallModuleSoftware({ packageId, level }));
  };

  const handleStartSoftware = (packageId: string, level: number) => {
    dispatch(startModuleSoftware({ packageId, level }));
  };

  const handleStopSoftware = (packageId: string, level: number) => {
    dispatch(stopModuleSoftware({ packageId, level }));
  };

  const handleSetExpertSkill = (instanceId: string, skill: string) => {
    dispatch(setExpertSkill({ instanceId, skill }));
  };

  const handleSetTranslatorLanguage = (instanceId: string, language: string) => {
    dispatch(setTranslatorLanguage({ instanceId, language }));
  };

  const handleSetComputerOptions = (computerId: string, options: string[]) => {
    dispatch(setComputerOptions({ computerId, options }));
  };

  const handleSetDatabaseTopic = (instanceId: string, topic: string) => {
    dispatch(setDatabaseTopic({ instanceId, topic }));
  };

  const handleSetVirtualCrewRoles = (instanceId: string, roles: string[]) => {
    dispatch(setVirtualCrewRoles({ instanceId, roles }));
  };

  const handleSetVirtualGunnerRoles = (instanceId: string, roles: string[]) => {
    dispatch(setVirtualGunnerRoles({ instanceId, roles }));
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };

  // Get available computers for dropdowns
  const availableComputers = Object.entries(COMPUTERS)
    .filter(([_, computer]) => computer.techLevel <= moduleConfig.techLevel)
    .map(([key, computer]) => ({
      key,
      model: computer.model,
      processing: computer.processing,
      cost: computer.baseCost,
      techLevel: computer.techLevel,
    }))
    .sort((a, b) => a.processing - b.processing); // Sort by processing (lowest first)

  // Get available backup computers (must have lower processing than primary)
  const availableBackupComputers = availableComputers.filter(comp => {
    if (!currentPrimary) return true;
    return comp.processing < currentPrimary.processing;
  });

  // Software helper functions
  const isSoftwareInstalled = (packageId: string, level: number): boolean => {
    return (softwareState?.installed || []).some(
      (s) => s.packageId === packageId && s.level === level && s.installed,
    );
  };

  const isSoftwareRunning = (packageId: string, level: number): boolean => {
    return (softwareState?.running || []).some(
      (s) => s.packageId === packageId && s.level === level && s.running,
    );
  };

  const isIncludedSoftware = (packageId: string): boolean => {
    const includedPackages = ["manoeuvre", "intellect", "library"];
    return includedPackages.includes(packageId);
  };

  const getInstalledInstances = (packageId: string, level: number) => {
    return (softwareState?.installed || []).filter(
      (s) => s.packageId === packageId && s.level === level && s.installed,
    );
  };

  // Get software categories
  const getSoftwareCategories = () => {
    const categories = new Set(SOFTWARE_PACKAGES.map(pkg => pkg.category));
    return Array.from(categories).sort();
  };

  // Filter software by category
  const getSoftwareByCategory = (category: string) => {
    if (category === "All" || category === "") {
      return SOFTWARE_PACKAGES;
    }
    return SOFTWARE_PACKAGES.filter(pkg => pkg.category === category);
  };

  const categories = ["All", ...getSoftwareCategories()];
  const filteredPackages = getSoftwareByCategory(selectedCategory);

  // Sort packages with included software first, then alphabetically
  const sortedPackages = [...filteredPackages].sort((a, b) => {
    const aIsIncluded = isIncludedSoftware(a.id);
    const bIsIncluded = isIncludedSoftware(b.id);
    
    // If one is included and the other isn't, included comes first
    if (aIsIncluded && !bIsIncluded) return -1;
    if (!aIsIncluded && bIsIncluded) return 1;
    
    // If both are included or both are not included, sort alphabetically
    return a.name.localeCompare(b.name);
  });

  const tabs = [
    { id: "computer", label: "Computer Systems", icon: "💻" },
    { id: "software", label: "Software Packages", icon: "📦" },
  ] as const;

  return (
    <div className="space-y-6">


      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="border-b border-gray-200 dark:border-gray-600 p-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Computer & Software Configuration
          </h2>
          
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "computer" && (
            <div className="space-y-6">
              {/* Computer Selection */}
              <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Select Computer Systems</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Primary Computer Selection */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300">Primary Computer</h4>
                    <select
                      value={primaryComputer}
                      onChange={(e) => {
                        setPrimaryComputer(e.target.value);
                        if (e.target.value) {
                          handleAddPrimaryComputer(e.target.value);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">Select primary computer...</option>
                      {availableComputers.map((comp) => (
                        <option key={comp.key} value={comp.key}>
                          {comp.model} (Processing: {comp.processing}, Cost: {formatCurrency(comp.cost)} Cr)
                        </option>
                      ))}
                    </select>
                    {currentPrimary && (
                      <div className="text-sm text-green-600 dark:text-green-400">
                        ✓ Primary computer installed: {currentPrimary.model}
                      </div>
                    )}
                  </div>

                  {/* Backup Computer Selection */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300">Backup Computer</h4>
                    <select
                      value={backupComputer}
                      onChange={(e) => {
                        setBackupComputer(e.target.value);
                        if (e.target.value) {
                          handleAddBackupComputer(e.target.value);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                      disabled={!currentPrimary}
                    >
                      <option value="">
                        {!currentPrimary ? "Select primary computer first..." : "Select backup computer..."}
                      </option>
                      {availableBackupComputers.map((comp) => (
                        <option key={comp.key} value={comp.key}>
                          {comp.model} (Processing: {comp.processing}, Cost: {formatCurrency(comp.cost)} Cr)
                        </option>
                      ))}
                    </select>
                    {currentBackup && (
                      <div className="text-sm text-green-600 dark:text-green-400">
                        ✓ Backup computer installed: {currentBackup.model}
                      </div>
                    )}
                    {!currentPrimary && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Select a primary computer first to enable backup selection
                      </div>
                    )}
                  </div>
                </div>

                {/* System Status */}
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-600 rounded-lg">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">System Status</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-300">Total Cost:</span>
                      <div className="text-gray-900 dark:text-gray-100">
                        {formatCurrency(installedSystems.reduce((sum: number, comp: any) => {
                          const baseCost = comp.baseCost || 0;
                          const options = moduleConfig.systems.computerOptions?.[comp.id] || [];
                          let costMultiplier = 1.0;
                          
                          if (options.includes("jumpControl") && options.includes("hardened")) {
                            costMultiplier = 2.0; // +100% for both options
                          } else if (options.includes("jumpControl") || options.includes("hardened")) {
                            costMultiplier = 1.5; // +50% for single option
                          }
                          
                          return sum + (baseCost * costMultiplier);
                        }, 0))} Cr
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-300">Computers:</span>
                      <div className="text-gray-900 dark:text-gray-100">
                        {installedSystems.length} installed
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-300">Status:</span>
                      <div className={`font-semibold ${
                        currentPrimary ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'
                      }`}>
                        {currentPrimary ? 'Operational' : 'No Primary Computer'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>



              {/* Current Computers */}
              {installedSystems.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">🔧 Installed Computers</h3>
                  <div className="space-y-4">
                    {installedSystems.map((computer: any, index: number) => (
                      <div key={computer.id || index} className="bg-white dark:bg-gray-800 rounded border dark:border-gray-600 overflow-hidden">
                        {/* Computer Header */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-600 border-b dark:border-gray-500">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {computer.model} 
                              <span className={`ml-2 px-2 py-1 text-xs rounded ${
                                computer.role === "primary" 
                                  ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200" 
                                  : "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                              }`}>
                                {computer.role === "primary" ? "Primary" : "Backup"}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveComputer(computer.id || `computer-${index}`)}
                            className="ml-4 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        
                        {/* Computer Details */}
                        <div className="p-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Left Column - Specifications */}
                            <div>
                              <div className="text-xs text-gray-500 mb-2">
                                Processing: {computer.processing} | 
                                Cost: {formatCurrency(computer.baseCost || 0)} Cr | 
                                Type: {computer.type} | 
                                TL: {computer.techLevel || 0}
                              </div>
                              
                              {/* Computer Cost with Options */}
                              {(() => {
                                const options = moduleConfig.systems.computerOptions?.[computer.id] || [];
                                const baseCost = computer.baseCost || 0;
                                let costMultiplier = 1.0;
                                
                                if (options.includes("jumpControl") && options.includes("hardened")) {
                                  costMultiplier = 2.0; // +100% for both options
                                } else if (options.includes("jumpControl") || options.includes("hardened")) {
                                  costMultiplier = 1.5; // +50% for single option
                                }
                                
                                const totalCost = baseCost * costMultiplier;
                                const costIncrease = totalCost - baseCost;
                                
                                return (
                                  <div className="text-xs text-gray-600">
                                    <div className="font-medium">Total Cost: {formatCurrency(totalCost)} Cr</div>
                                    {costIncrease > 0 && (
                                      <div className="text-blue-600">
                                        +{formatCurrency(costIncrease)} Cr from options
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                            
                            {/* Right Column - Computer Options */}
                            <div>
                              <div className="text-xs font-medium text-gray-700 mb-2">Computer Options:</div>
                              <div className="space-y-2">
                                {["jumpControl", "hardened"].map((optionKey) => {
                                  const option = COMPUTER_OPTIONS[optionKey as keyof typeof COMPUTER_OPTIONS];
                                  const isSelected = moduleConfig.systems.computerOptions?.[computer.id]?.includes(optionKey) || false;
                                  
                                  return (
                                    <label key={optionKey} className="flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          const currentOptions = moduleConfig.systems.computerOptions?.[computer.id] || [];
                                          const newOptions = e.target.checked
                                            ? [...currentOptions, optionKey]
                                            : currentOptions.filter(opt => opt !== optionKey);
                                          handleSetComputerOptions(computer.id, newOptions);
                                        }}
                                        className="mr-2"
                                      />
                                      <span className="text-sm text-gray-700">
                                        {option.name}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "software" && (
            <div className="space-y-6">
              {/* Software Selection */}
              <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Software Packages</h3>
                
                {!currentPrimary ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>Install a primary computer first to configure software</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Category Filter */}
                    <div className="flex flex-wrap gap-2">
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                            selectedCategory === category
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>

                    {/* Software Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sortedPackages.map((software) => {
                        const isIncluded = isIncludedSoftware(software.id);
                        const isOverTechLevel = software.tl > moduleConfig.techLevel;

                        // Get all available levels for this software
                        const availableLevels = software.maxLevel !== undefined 
                          ? Array.from({ length: software.maxLevel + 1 }, (_, i) => i)
                          : [0];

                        return (
                          <div
                            key={software.id}
                            className={`border dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between ${
                              isOverTechLevel ? "bg-gray-100 dark:bg-gray-700 opacity-60" : "bg-gray-50 dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500"
                            }`}
                          >
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                {software.name}
                                {isIncluded && (
                                  <span className="ml-2 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
                                    Included
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                <div>Category: {software.category}</div>
                                <div>TL: {software.tl}</div>
                                {software.maxLevel !== undefined && (
                                  <div>Levels: 0-{software.maxLevel}</div>
                                )}
                              </div>

                              {/* Expert Skill Selection */}
                              {software.id === "expert" && (
                                <div className="mt-3 p-2 bg-blue-50 rounded border">
                                  <div className="text-xs font-medium text-blue-800 mb-2">Enter Skill:</div>
                                  <input
                                    type="text"
                                    value={softwareState?.expertSkills?.[software.id] || ""}
                                    onChange={(e) => handleSetExpertSkill(software.id, e.target.value)}
                                    placeholder="e.g., Pilot, Medic, Electronics (Computers)..."
                                    className="w-full px-2 py-1 border border-blue-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                  <div className="text-xs text-blue-600 mt-1">
                                    Enter the specific skill this Expert software will simulate
                                  </div>
                                </div>
                              )}

                              {/* Translator Language Selection */}
                              {software.id === "translator" && (
                                <div className="mt-3 p-2 bg-green-50 rounded border">
                                  <div className="text-xs font-medium text-green-800 mb-2">Enter Language:</div>
                                  <input
                                    type="text"
                                    value={softwareState?.translatorLanguages?.[software.id] || ""}
                                    onChange={(e) => handleSetTranslatorLanguage(software.id, e.target.value)}
                                    placeholder="e.g., Anglic, Vilani, Aslan..."
                                    className="w-full px-2 py-1 border border-green-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                                  />
                                  <div className="text-xs text-green-600 mt-1">
                                    Enter the specific language this Translator software will handle
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            <div className="mt-3 space-y-3">
                              {/* Install Dropdown */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Select Level:
                                </label>
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const level = parseInt(e.target.value);
                                      handleInstallSoftware(software.id, level);
                                      e.target.value = ""; // Reset selection
                                    }
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  defaultValue=""
                                >
                                  <option value="">Select level...</option>
                                  {availableLevels
                                    .map((level) => {
                                      const levelInfo = software.levels?.find(l => l.level === level) || software;
                                      const isInstalled = isSoftwareInstalled(software.id, level);
                                      const isLevelOverTechLevel = levelInfo.tl > moduleConfig.techLevel;
                                      
                                      if (isInstalled) return null;
                                      
                                      return {
                                        level,
                                        levelInfo,
                                        isLevelOverTechLevel,
                                        bandwidth: levelInfo.bandwidth,
                                        techLevel: levelInfo.tl,
                                        cost: levelInfo.cost
                                      };
                                    })
                                    .filter(Boolean)
                                    .sort((a, b) => {
                                      // Sort by Bandwidth (ascending), then Tech Level (ascending), then Cost (ascending)
                                      if (a!.bandwidth !== b!.bandwidth) {
                                        return a!.bandwidth - b!.bandwidth;
                                      }
                                      if (a!.techLevel !== b!.techLevel) {
                                        return a!.techLevel - b!.techLevel;
                                      }
                                      return a!.cost - b!.cost;
                                    })
                                    .map((item) => (
                                      <option 
                                        key={item!.level} 
                                        value={item!.level}
                                        disabled={item!.isLevelOverTechLevel}
                                      >
                                        Level {item!.level} • Bandwidth: {item!.bandwidth} • TL: {item!.techLevel} • Cost: {formatCurrency(item!.cost * 1000000)} Cr
                                        {item!.isLevelOverTechLevel ? ` (Requires TL ${item!.techLevel})` : ''}
                                      </option>
                                    ))}
                                </select>
                              </div>

                              {/* Installed Levels Management */}
                              {availableLevels.some(level => isSoftwareInstalled(software.id, level)) && (
                                <div className="border-t pt-2">
                                  <div className="text-xs font-medium text-gray-700 mb-2">Installed Levels:</div>
                                  {availableLevels.map((level) => {
                                    const isInstalled = isSoftwareInstalled(software.id, level);
                                    const isRunning = isSoftwareRunning(software.id, level);
                                    
                                    if (!isInstalled) return null;
                                    
                                    return (
                                      <div key={level} className="flex items-center justify-between p-2 bg-gray-50 rounded mb-1">
                                        <div className="text-xs">
                                          <span className="font-medium">Level {level}</span>
                                          <span className={`ml-2 px-1 py-0.5 text-xs rounded ${
                                            isRunning ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                          }`}>
                                            {isRunning ? 'Running' : 'Stopped'}
                                          </span>
                                        </div>
                                        <div className="flex space-x-1">
                                          <button
                                            onClick={() => isRunning ? handleStopSoftware(software.id, level) : handleStartSoftware(software.id, level)}
                                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                              isRunning
                                                ? "bg-red-500 text-white hover:bg-red-600"
                                                : "bg-green-500 text-white hover:bg-green-600"
                                            }`}
                                          >
                                            {isRunning ? "Stop" : "Start"}
                                          </button>
                                          <button
                                            onClick={() => handleUninstallSoftware(software.id, level)}
                                            className="px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs"
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Software Configuration for Each Instance */}
                              {availableLevels.some(level => isSoftwareInstalled(software.id, level)) && (
                                <div className="border-t pt-2 mt-2">
                                  <div className="text-xs font-medium text-blue-800 mb-2">Installed Instances:</div>
                                  {availableLevels.map((level) => {
                                    const instances = getInstalledInstances(software.id, level);
                                    return instances.map((instance, index) => (
                                      <div key={instance.instanceId} className="mb-3 p-2 bg-blue-50 rounded border">
                                        <div className="text-xs font-medium text-blue-700 mb-2">
                                          Instance {index + 1} (Level {level})
                                        </div>
                                        
                                        {/* Expert Software Configuration */}
                                        {software.id === "expert" && (
                                          <div className="space-y-2">
                                            <label className="text-xs font-medium text-blue-800">
                                              Skill:
                                            </label>
                                            <input
                                              type="text"
                                              placeholder="Enter skill (e.g., Pilot, Engineer, Gunner)"
                                              value={softwareState?.expertSkills?.[instance.instanceId || ''] || ""}
                                              onChange={(e) => handleSetExpertSkill(instance.instanceId || '', e.target.value)}
                                              className="w-full px-2 py-1 text-xs border rounded"
                                            />
                                          </div>
                                        )}

                                        {/* Translator Software Configuration */}
                                        {software.id === "translator" && (
                                          <div className="space-y-2">
                                            <label className="text-xs font-medium text-blue-800">
                                              Language:
                                            </label>
                                            <input
                                              type="text"
                                              placeholder="Enter language (e.g., Vilani, Aslan, Vargr)"
                                              value={softwareState?.translatorLanguages?.[instance.instanceId || ''] || ""}
                                              onChange={(e) => handleSetTranslatorLanguage(instance.instanceId || '', e.target.value)}
                                              className="w-full px-2 py-1 text-xs border rounded"
                                            />
                                          </div>
                                        )}

                                        {/* Database Software Configuration */}
                                        {software.id === "database" && (
                                          <div className="space-y-2">
                                            <label className="text-xs font-medium text-blue-800">
                                              Topic:
                                            </label>
                                            <input
                                              type="text"
                                              placeholder="Enter database topic (e.g., Starship Engineering, Alien Cultures)"
                                              value={softwareState?.databaseTopics?.[instance.instanceId || ''] || ""}
                                              onChange={(e) => handleSetDatabaseTopic(instance.instanceId || '', e.target.value)}
                                              className="w-full px-2 py-1 text-xs border rounded"
                                            />
                                          </div>
                                        )}

                                        {/* Virtual Crew Configuration */}
                                        {software.id === "virtualCrew" && (
                                          <div className="space-y-2">
                                            <label className="text-xs font-medium text-blue-800">
                                              Crew Roles to Replace:
                                            </label>
                                            <div className="space-y-1">
                                              {["pilot", "sensorOperator"].map((role) => (
                                                <label key={role} className="flex items-center">
                                                  <input
                                                    type="checkbox"
                                                    checked={softwareState?.virtualCrewRoles?.[instance.instanceId || '']?.includes(role) || false}
                                                    onChange={(e) => {
                                                      const currentRoles = softwareState?.virtualCrewRoles?.[instance.instanceId || ''] || [];
                                                      const newRoles = e.target.checked
                                                        ? [...currentRoles, role]
                                                        : currentRoles.filter(r => r !== role);
                                                      handleSetVirtualCrewRoles(instance.instanceId || '', newRoles);
                                                    }}
                                                    className="mr-2"
                                                  />
                                                  <span className="text-xs text-blue-700 capitalize">
                                                    {role.replace(/([A-Z])/g, ' $1').trim()}
                                                  </span>
                                                </label>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Virtual Gunner Configuration */}
                                        {software.id === "virtualGunner" && (
                                          <div className="space-y-2">
                                            <label className="text-xs font-medium text-blue-800">
                                              Gunner Roles to Replace:
                                            </label>
                                            <div className="space-y-1">
                                              {["turret", "barbette", "bay", "screen"].map((role) => (
                                                <label key={role} className="flex items-center">
                                                  <input
                                                    type="checkbox"
                                                    checked={softwareState?.virtualGunnerRoles?.[instance.instanceId || '']?.includes(role) || false}
                                                    onChange={(e) => {
                                                      const currentRoles = softwareState?.virtualGunnerRoles?.[instance.instanceId || ''] || [];
                                                      const newRoles = e.target.checked
                                                        ? [...currentRoles, role]
                                                        : currentRoles.filter(r => r !== role);
                                                      handleSetVirtualGunnerRoles(instance.instanceId || '', newRoles);
                                                    }}
                                                    className="mr-2"
                                                  />
                                                  <span className="text-xs text-blue-700 capitalize">
                                                    {role}
                                                  </span>
                                                </label>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ));
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Software Summary */}
              {currentPrimary && (
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Software Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Installed:</span>
                      <div className="text-gray-900">
                        {(softwareState?.installed || []).filter(s => s.installed).length} packages
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Running:</span>
                      <div className="text-gray-900">
                        {(softwareState?.running || []).filter(s => s.running).length} packages
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Total Cost:</span>
                      <div className="text-gray-900">
                        {formatCurrency(
                          (softwareState?.installed || [])
                            .filter(s => s.installed)
                            .reduce((sum, s) => {
                              const softwareInfo = SOFTWARE_PACKAGES.find(pkg => pkg.id === s.packageId);
                              if (!softwareInfo) return sum;
                              const levelInfo = softwareInfo.levels?.find(l => l.level === s.level) || softwareInfo;
                              return sum + levelInfo.cost * 1000000;
                            }, 0)
                        )} Cr
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Status:</span>
                      <div className="font-semibold text-green-600">
                        Software Ready
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
